import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Form, useNavigate, useSearchParams } from 'react-router'
import { z } from 'zod'

import { Page } from '#app/components/page.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Divider } from '#app/components/ui/divider.tsx'
import { type Route } from '#app/routes/mocra+/_form+/+types/records-access.ts'
import { createContactReferenceDefinition } from '#app/routes/mocra+/_form+/components/contact-references/definition.ts'
import { ContactEditOverlay } from '#app/routes/mocra+/_form+/components/contacts/edit.tsx'
import {
	createEmptyContactDraft,
	type ContactDraft,
} from '#app/routes/mocra+/_form+/components/contacts/types.ts'
import { REPOSITORY_SYSTEM_OPTIONS } from '#app/routes/mocra+/_form+/components/repositories/constants.ts'
import { repositoryDefinition } from '#app/routes/mocra+/_form+/components/repositories/definition.ts'
import { RepositoryEditOverlay } from '#app/routes/mocra+/_form+/components/repositories/edit.tsx'
import {
	RepositoryEntrySchema,
	type RepositoryEntry,
} from '#app/routes/mocra+/_form+/components/repositories/schema.ts'
import {
	createRepositoryDraft,
	repositoryDraftToEntry,
	repositoryEntryToDraft as ignoredRepositoryEntryToDraft,
	sanitizeRepositoryDraft,
	type RepositoryDraft,
} from '#app/routes/mocra+/_form+/components/repositories/types.ts'
import {
	type ContactDirectoryEntry,
	type ContactInput,
} from '#app/routes/mocra+/_form+/contact-directory.ts'
import {
	normalizeContactReference,
	referenceToContactDraft,
	resolveContactDisplay,
	type ContactDisplay,
	type ContactReference,
} from '#app/routes/mocra+/_form+/contacts/reference.ts'
import {
	buildStepPathFromId,
	getNextFormStepId,
} from '#app/routes/mocra+/_form+/form-steps.ts'
import { useContactDirectory } from '#app/routes/mocra+/_form+/hooks/use-contact-directory.ts'
import { useEntityCollection } from '#app/routes/mocra+/_form+/hooks/use-entity-collection.ts'
import { useSectionForm } from '#app/routes/mocra+/_form+/hooks/use-section-form.ts'
import {
	DRILL_CADENCE_OPTIONS,
	DRILL_CADENCE_VALUE_SET,
} from '#app/routes/mocra+/_form+/intake-options.ts'
import { RecordCustodiansSection } from '#app/routes/mocra+/_form+/records-access/record-custodians-section.tsx'
import { RecordRepositoriesSection } from '#app/routes/mocra+/_form+/records-access/record-repositories-section.tsx'
import { SecureRecordTransferSection } from '#app/routes/mocra+/_form+/records-access/secure-transfer-section.tsx'
import { createRouteSectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'MoCRA — Records Access' },
]

export const RECORDS_ACCESS_SECTION_KEY = 'recordsAccess' as const
const STEP_ID = 'records-access'
const RECORDS_ACCESS_RESOURCES = createRouteSectionResources({
	route: '/mocra/records-access',
})

const SECURE_TRANSFER_METHOD_OPTIONS = [
	{ value: 'secure_portal', label: 'Secure portal / data room' },
	{ value: 'encrypted_email', label: 'Encrypted email package' },
	{ value: 'on_site_review', label: 'On-site review only' },
	{ value: 'courier', label: 'Encrypted media courier' },
] as const

export const CustodianReferenceSchema = z.object({
	contactId: z
		.string({
			required_error: 'Select who can retrieve records during inspections.',
		})
		.min(1, 'Select who can retrieve records during inspections.'),
	fallbackName: z.string().optional(),
	fallbackEmail: z.string().email().optional(),
	fallbackTitle: z.string().optional(),
	fallbackPhone: z.string().optional(),
})

export const RecordsAccessSchema = z.object({
	repositories: z.array(RepositoryEntrySchema).default([]),
	custodians: z.array(CustodianReferenceSchema).default([]),
	secureTransferMethod: z.enum(
		SECURE_TRANSFER_METHOD_OPTIONS.map((option) => option.value) as [
			(typeof SECURE_TRANSFER_METHOD_OPTIONS)[number]['value'],
			...string[],
		],
	),
	drillCadence: z
		.string({
			required_error: 'Select how often you rehearse record-access drills.',
		})
		.refine(
			(value) => DRILL_CADENCE_VALUE_SET.has(value),
			'Pick the cadence that matches your practice schedule.',
		),
})

export type RecordsAccessFormValues = z.infer<typeof RecordsAccessSchema>
type CustodianReference = ContactReference

export const RECORDS_ACCESS_DEFAULTS: RecordsAccessFormValues = {
	repositories: [],
	custodians: [],
	secureTransferMethod: 'secure_portal',
	drillCadence: 'quarterly',
}

type MaybeString = string | null | undefined
const DEFAULT_CUSTODIAN_TITLE = 'Records custodian'
const custodianDefinition = createContactReferenceDefinition({
	defaultTitle: DEFAULT_CUSTODIAN_TITLE,
})

export default function RecordsAccess() {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const form = useSectionForm({
		section: RECORDS_ACCESS_SECTION_KEY,
		schema: RecordsAccessSchema,
		defaultValue: RECORDS_ACCESS_DEFAULTS,
		onValidSubmit: () => {
			const nextStepId = getNextFormStepId(STEP_ID)
			if (!nextStepId) return
			const nextHref = buildStepPathFromId(nextStepId, searchParams)
			void navigate(nextHref)
		},
	})
	const {
		values,
		setValues,
		setFieldValue,
		getFieldErrors,
		submit,
		status,
		hasStoredValue,
		clear,
	} = form

	const didNormalizeRef = useRef(false)
	useEffect(() => {
		if (didNormalizeRef.current) return
		const normalized = normalizeRecordsAccessDraft(values)
		if (normalized && !recordsAccessValuesEqual(normalized, values)) {
			didNormalizeRef.current = true
			setValues(normalized)
			return
		}
		didNormalizeRef.current = true
		if (
			!normalized &&
			!recordsAccessValuesEqual(RECORDS_ACCESS_DEFAULTS, values)
		) {
			setValues(RECORDS_ACCESS_DEFAULTS)
		}
	}, [setValues, values])

	const repositoriesCollection = useEntityCollection<
		RecordsAccessFormValues,
		RepositoryEntry,
		RepositoryDraft
	>({
		form,
		fieldPath: 'repositories',
		definition: repositoryDefinition,
		getFieldErrorPaths: getRepositoryFieldErrorPaths,
	})
	const custodiansCollection = useEntityCollection<
		RecordsAccessFormValues,
		ContactReference,
		ContactDraft
	>({
		form,
		fieldPath: 'custodians',
		definition: custodianDefinition,
		getFieldErrorPaths: getCustodianFieldErrorPaths,
	})
	const custodianReferences = useMemo(
		() => values.custodians ?? [],
		[values.custodians],
	)

	const [editingRepositoryIndex, setEditingRepositoryIndex] = useState<
		number | null
	>(null)
	const editingRepositoryDraft =
		editingRepositoryIndex !== null &&
		repositoriesCollection.items[editingRepositoryIndex]
			? repositoriesCollection.items[editingRepositoryIndex].draft
			: repositoriesCollection.createDraft()
	const repositoryItems = repositoriesCollection.items.map((item) => ({
		draft: item.draft,
		errors: item.errors,
	}))

	const handleAddRepository = (draft: RepositoryDraft) => {
		repositoriesCollection.append(draft)
	}

	const handleEditRepository = (draft: RepositoryDraft) => {
		if (editingRepositoryIndex === null) return
		repositoriesCollection.replace(editingRepositoryIndex, draft)
		setEditingRepositoryIndex(null)
	}

	const handleRemoveRepository = (index: number) => {
		repositoriesCollection.remove(index)
		setEditingRepositoryIndex(null)
	}

	const { contacts: directoryContacts, mergeContacts: mergeDirectoryContacts } =
		useContactDirectory()
	const mergeCustodianSignatureRef = useRef<string | null>(null)

	const directoryById = useMemo(() => {
		const map = new Map<string, ContactDirectoryEntry>()
		for (const entry of directoryContacts) {
			map.set(entry.id, entry)
		}
		return map
	}, [directoryContacts])

	const custodianItems = custodiansCollection.items

	const resolvedCustodians = useMemo(
		() =>
			custodianReferences.map((reference) =>
				resolveContactDisplay(reference, directoryById, {
					defaultName: DEFAULT_CUSTODIAN_TITLE,
					defaultTitle: DEFAULT_CUSTODIAN_TITLE,
				}),
			),
		[custodianReferences, directoryById],
	)

	const existingContactDrafts = useMemo(
		() =>
			custodianItems.map((item, index) => {
				const display =
					resolvedCustodians[index] ??
					resolveContactDisplay(item.entry, directoryById, {
						defaultName: DEFAULT_CUSTODIAN_TITLE,
						defaultTitle: DEFAULT_CUSTODIAN_TITLE,
					})
				const draft = referenceToContactDraft(item.entry, display)
				return {
					...draft,
					title: display.title,
					name: display.name,
					email: display.email,
					phone: display.phone,
				}
			}),
		[custodianItems, directoryById, resolvedCustodians],
	)

	useEffect(() => {
		if (custodianReferences.length === 0) return
		const signature = custodianReferences
			.map(
				(reference) =>
					`${reference.contactId}|${reference.fallbackName ?? ''}|${reference.fallbackEmail ?? ''}|${reference.fallbackTitle ?? ''}|${reference.fallbackPhone ?? ''}`,
			)
			.join('||')
		if (mergeCustodianSignatureRef.current === signature) return
		mergeCustodianSignatureRef.current = signature
		const inputs = custodianReferences
			.map((reference, index) =>
				referenceToDirectoryInput(
					reference,
					resolvedCustodians[index] ??
						resolveContactDisplay(reference, directoryById, {
							defaultName: DEFAULT_CUSTODIAN_TITLE,
							defaultTitle: DEFAULT_CUSTODIAN_TITLE,
						}),
				),
			)
			.filter(
				(input): input is ContactInput =>
					input !== null && Boolean(input.email),
			)
		if (inputs.length === 0) return
		mergeDirectoryContacts(inputs, { source: 'Records access' })
	}, [
		custodianReferences,
		directoryById,
		mergeDirectoryContacts,
		resolvedCustodians,
	])

	const [editingCustodianIndex, setEditingCustodianIndex] = useState<
		number | null
	>(null)
	const editingCustodianDraft =
		editingCustodianIndex !== null &&
		existingContactDrafts[editingCustodianIndex]
			? existingContactDrafts[editingCustodianIndex]
			: createEmptyContactDraft()

	const handleAddCustodian = (draft: ContactDraft) => {
		if (!draft.id || draft.id.trim().length === 0) {
			throw new Error('Custodian contacts must have a stable contact id.')
		}
		custodiansCollection.append(draft)
	}

	const handleEditCustodian = (draft: ContactDraft) => {
		if (editingCustodianIndex === null) return
		if (!draft.id || draft.id.trim().length === 0) {
			throw new Error('Custodian contacts must have a stable contact id.')
		}
		custodiansCollection.replace(editingCustodianIndex, draft)
		setEditingCustodianIndex(null)
	}

	const handleRemoveCustodian = (index: number) => {
		custodiansCollection.remove(index)
		setEditingCustodianIndex(null)
	}

	const secureTransferErrors = getFieldErrors('secureTransferMethod')
	const drillCadenceErrors = getFieldErrors('drillCadence')

	return (
		<Page
			title="Records Access"
			description="Capture where regulated records live, who can retrieve them, and how you securely hand them to inspectors."
			className="space-y-8"
		>
			<Form
				method="post"
				className="space-y-8"
				onSubmit={(event: FormEvent<HTMLFormElement>) => {
					event.preventDefault()
					void submit()
				}}
			>
				<Divider />
				<RecordRepositoriesSection
					repositories={repositoryItems}
					resources={RECORDS_ACCESS_RESOURCES}
					onAdd={handleAddRepository}
					onEdit={(index) => setEditingRepositoryIndex(index)}
					onRemove={handleRemoveRepository}
				/>
				<RepositoryEditOverlay
					open={editingRepositoryIndex !== null}
					onOpenChange={(open) => {
						if (!open) {
							setEditingRepositoryIndex(null)
						}
					}}
					onSubmit={handleEditRepository}
					initialValue={editingRepositoryDraft}
					onClosed={() => setEditingRepositoryIndex(null)}
				/>

				<Divider />

				<RecordCustodiansSection
					references={custodianReferences}
					displays={resolvedCustodians}
					resources={RECORDS_ACCESS_RESOURCES}
					getFieldErrors={getFieldErrors}
					onAdd={handleAddCustodian}
					onEdit={(index) => setEditingCustodianIndex(index)}
					onRemove={handleRemoveCustodian}
					existingContactDrafts={existingContactDrafts}
					directorySource="Records access"
				/>
				<ContactEditOverlay
					open={editingCustodianIndex !== null}
					onOpenChange={(open) => {
						if (!open) {
							setEditingCustodianIndex(null)
						}
					}}
					onSubmit={handleEditCustodian}
					initialValue={editingCustodianDraft}
					directorySource="Records access"
					existingContacts={existingContactDrafts.filter(
						(_, idx) => idx !== editingCustodianIndex,
					)}
					onClosed={() => setEditingCustodianIndex(null)}
					onCancel={() => setEditingCustodianIndex(null)}
				/>

				<Divider />

				<SecureRecordTransferSection
					resources={RECORDS_ACCESS_RESOURCES}
					secureTransferMethod={values.secureTransferMethod}
					drillCadence={values.drillCadence}
					secureTransferOptions={SECURE_TRANSFER_METHOD_OPTIONS}
					drillCadenceOptions={DRILL_CADENCE_OPTIONS}
					secureTransferErrors={secureTransferErrors}
					drillCadenceErrors={drillCadenceErrors}
					onSecureTransferChange={(next) =>
						setFieldValue(
							'secureTransferMethod',
							resolveSecureTransferMethodValue(next),
						)
					}
					onDrillCadenceChange={(next) =>
						setFieldValue('drillCadence', resolveDrillCadenceValue(next))
					}
				/>

				{status === 'invalid' ? (
					<p className="text-destructive text-sm">
						Check the highlighted fields before continuing.
					</p>
				) : null}

				<div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
					{hasStoredValue ? (
						<Button type="button" variant="outline" onClick={() => clear()}>
							Clear answers
						</Button>
					) : (
						<span />
					)}
					<Button type="submit" className="w-full sm:w-auto">
						Continue
					</Button>
				</div>
			</Form>
		</Page>
	)
}

function getRepositoryFieldErrorPaths(index: number): readonly string[] {
	const prefix = `repositories.${index}`
	return [prefix, `${prefix}.system`, `${prefix}.location`]
}

function getCustodianFieldErrorPaths(index: number): readonly string[] {
	const prefix = `custodians.${index}`
	return [prefix, `${prefix}.contactId`]
}

function normalizeRecordsAccessDraft(
	raw: unknown,
): RecordsAccessFormValues | null {
	if (!raw) return null
	const parsed = RecordsAccessSchema.safeParse(raw)
	if (parsed.success) return parsed.data
	if (typeof raw !== 'object' || raw === null) return null
	const legacy = raw as Record<string, unknown>
	const repositories = Array.isArray(legacy.repositories)
		? legacy.repositories
				.map((entry) => legacyRepositoryToEntry(entry))
				.filter((entry): entry is RepositoryEntry => entry !== null)
		: []
	const custodians = Array.isArray(legacy.custodians)
		? legacy.custodians
				.map((entry) => legacyCustodianToReference(entry))
				.filter(
					(reference): reference is CustodianReference => reference !== null,
				)
		: []
	const secureTransferMethod = resolveSecureTransferMethodValue(
		legacy.secureTransferMethod,
	)
	const drillCadence = resolveDrillCadenceValue(legacy.drillCadence)
	return {
		repositories,
		custodians,
		secureTransferMethod,
		drillCadence,
	}
}

function recordsAccessValuesEqual(
	left: RecordsAccessFormValues,
	right: RecordsAccessFormValues,
): boolean {
	return JSON.stringify(left) === JSON.stringify(right)
}

function legacyRepositoryToEntry(value: unknown): RepositoryEntry | null {
	if (!value || typeof value !== 'object') return null
	const raw = value as Record<string, unknown>
	const systemRaw = toOptionalString(raw.system as MaybeString)
	const locationRaw = toOptionalString(raw.location as MaybeString)
	if (!locationRaw) return null
	const defaultSystem = REPOSITORY_SYSTEM_OPTIONS[0]?.value ?? 'sharepoint'
	const systemOption = systemRaw
		? REPOSITORY_SYSTEM_OPTIONS.some((option) => option.value === systemRaw)
			? (systemRaw as RepositoryDraft['system'])
			: (defaultSystem as RepositoryDraft['system'])
		: (defaultSystem as RepositoryDraft['system'])
	const draft = sanitizeRepositoryDraft(
		createRepositoryDraft({
			system: systemOption,
			location: locationRaw,
		}),
	)
	return repositoryDraftToEntry(draft)
}

function legacyCustodianToReference(value: unknown): CustodianReference | null {
	if (!value || typeof value !== 'object') return null
	const raw = value as Record<string, unknown>
	const contactId =
		toOptionalString(raw.contactId as MaybeString) ?? generateContactId()
	const fallbackName = toOptionalString(
		(raw.fallbackName ?? raw.name) as MaybeString,
	)
	const fallbackEmail = toOptionalString(
		(raw.fallbackEmail ?? raw.email) as MaybeString,
	)
	const fallbackTitle = toOptionalString(
		(raw.fallbackTitle ?? raw.title) as MaybeString,
	)
	const fallbackPhone = toOptionalString(
		(raw.fallbackPhone ?? raw.phone) as MaybeString,
	)
	return normalizeContactReference({
		contactId,
		fallbackName,
		fallbackEmail,
		fallbackTitle,
		fallbackPhone,
	})
}

function generateContactId(): string {
	if (
		typeof crypto !== 'undefined' &&
		typeof crypto.randomUUID === 'function'
	) {
		return crypto.randomUUID()
	}
	return `contact-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function referenceToDirectoryInput(
	reference: CustodianReference,
	display: ContactDisplay,
): ContactInput | null {
	const email = reference.fallbackEmail ?? display.email
	if (!email) return null
	return {
		id: reference.contactId,
		name: reference.fallbackName ?? display.name,
		email,
		title: reference.fallbackTitle ?? display.title,
		phone: reference.fallbackPhone ?? display.phone,
	}
}

function resolveSecureTransferMethodValue(
	input: unknown,
): RecordsAccessFormValues['secureTransferMethod'] {
	const raw =
		typeof input === 'string'
			? input
			: typeof input === 'object' && input !== null && 'value' in input
				? (input as { value?: unknown }).value
				: undefined
	return isSecureTransferMethodValue(raw)
		? raw
		: (SECURE_TRANSFER_METHOD_OPTIONS[0]?.value ?? 'secure_portal')
}

function resolveDrillCadenceValue(
	input: unknown,
): RecordsAccessFormValues['drillCadence'] {
	const raw =
		typeof input === 'string'
			? input
			: typeof input === 'object' && input !== null && 'value' in input
				? (input as { value?: unknown }).value
				: undefined
	return typeof raw === 'string' && DRILL_CADENCE_VALUE_SET.has(raw)
		? raw
		: 'quarterly'
}

function isSecureTransferMethodValue(
	value: unknown,
): value is RecordsAccessFormValues['secureTransferMethod'] {
	if (typeof value !== 'string') return false
	return SECURE_TRANSFER_METHOD_OPTIONS.some((option) => option.value === value)
}

function toOptionalString(value: MaybeString): string | undefined {
	if (typeof value !== 'string') return undefined
	const trimmed = value.trim()
	return trimmed.length > 0 ? trimmed : undefined
}
